import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AcademicHolidayListResponse, AcademicHolidayModel, AcademicHolidayPayLoad } from "../../models/academic-holidays/academic_holidays.model";

const SKIP_GLOBAL_ERROR_DIALOG_HEADER = 'X-Skip-Global-Error-Dialog';

interface AcademicHolidayListParams {
    academic_term_id?: number | string | null;
    year?: string | null;
    keyword?: string | null;
    keywords?: string | null;
    page?: number;
    limit?: number;
    sort?: string | null;
    direction?: string | null;
}

@Injectable({
    providedIn: "root",
})
export class AcademicHolidayService {
    private apiUrl = "/academic_holidays";

    #http = inject(HttpClient);

    getAcademicHolidays(params?: AcademicHolidayListParams): Observable<AcademicHolidayModel[]> {
        return this.getAcademicHolidaysList(params).pipe(
            map((res) => this.normalizeAcademicHolidayResponse(res))
        );
    }

    getAcademicHolidaysList(params?: AcademicHolidayListParams): Observable<AcademicHolidayListResponse> {
        return this.#http.get<unknown>(this.apiUrl, {
            params: this.buildListParams(params)
        }).pipe(
            map((res) => this.normalizeAcademicHolidayListResponse(res, params))
        );
    }

    createAcademicHoliday(payload: AcademicHolidayPayLoad): Observable<any> {
        return this.#http.post<AcademicHolidayPayLoad>(this.apiUrl, payload, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }

    updateAcademicHoliday(id: number, payload: AcademicHolidayPayLoad): Observable<any> {
        return this.#http.put<AcademicHolidayPayLoad>(`${this.apiUrl}/${id}`, payload, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }

    deleteAcademicHoliday(id: number): Observable<any> {
        return this.#http.delete(`${this.apiUrl}/${id}`, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }

    exportTemplate(): Observable<Blob> {
        return this.#http.get(`${this.apiUrl}/export_template`, {
            responseType: "blob",
        });
    }

    importAcademicHolidays(file: File): Observable<any> {
        const formData = new FormData();

        formData.append('file', file);

        return this.#http.post(`${this.apiUrl}/import`, formData, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }

    getAcademicHolidayById(id: number): Observable<AcademicHolidayModel> {
        return this.#http.get<unknown>(`${this.apiUrl}/${id}`).pipe(
            map((res) => this.normalizeAcademicHolidayDetailResponse(res))
        );
    }

    private buildListParams(params?: AcademicHolidayListParams): HttpParams {
        let httpParams = new HttpParams();

        if (params?.academic_term_id != null && params.academic_term_id !== '') {
            httpParams = httpParams.set('academic_term_id', String(params.academic_term_id));
        }

        if (params?.year != null && params.year !== '') {
            httpParams = httpParams.set('year', params.year);
        }

        if (params?.keyword != null && params.keyword !== '') {
            httpParams = httpParams.set('keyword', params.keyword);
        }

        if (params?.keywords != null && params.keywords !== '') {
            httpParams = httpParams.set('keywords', params.keywords);
        }

        httpParams = this.setParamIfPresent(httpParams, 'page', params?.page);
        httpParams = this.setParamIfPresent(httpParams, 'limit', params?.limit);
        httpParams = this.setParamIfPresent(httpParams, 'sort', params?.sort);
        httpParams = this.setParamIfPresent(httpParams, 'direction', params?.direction);

        return httpParams;
    }

    private setParamIfPresent(
        httpParams: HttpParams,
        key: string,
        value: string | number | null | undefined
    ): HttpParams {
        if (value == null || value === '') {
            return httpParams;
        }

        return httpParams.set(key, String(value));
    }

    private normalizeAcademicHolidayListResponse(
        res: unknown,
        params?: AcademicHolidayListParams
    ): AcademicHolidayListResponse {
        if (res !== null && typeof res === "object" && !Array.isArray(res)) {
            const response = res as Record<string, unknown>;

            if (Array.isArray(response.data)) {
                return {
                    data: response.data as AcademicHolidayModel[],
                    page: Number(response.page ?? params?.page ?? 1),
                    limit: Number(response.limit ?? params?.limit ?? 10),
                    total: Number(response.total ?? response.data.length),
                    total_pages: Number(response.total_pages ?? 1),
                    can_import_for_year: response.can_import_for_year === true,
                };
            }
        }

        const data = this.normalizeAcademicHolidayResponse(res);
        const limit = params?.limit ?? 10;

        return {
            data,
            page: params?.page ?? 1,
            limit,
            total: data.length,
            total_pages: Math.max(1, Math.ceil(data.length / limit)),
            can_import_for_year: false,
        };
    }

    private normalizeAcademicHolidayResponse(res: unknown): AcademicHolidayModel[] {
        if (Array.isArray(res)) {
            return res as AcademicHolidayModel[];
        }

        if (res === null || typeof res !== "object") {
            return [];
        }

        const response = res as Record<string, unknown>;
        const keys = ["data", "academic_holidays", "items", "records", "results", "rows"];

        for (const key of keys) {
            const value = response[key];

            if (Array.isArray(value)) {
                return value as AcademicHolidayModel[];
            }
        }

        for (const key of keys) {
            const value = response[key];

            if (value !== null && typeof value === "object") {
                const nestedValue = this.normalizeAcademicHolidayResponse(value);

                if (nestedValue.length > 0) {
                    return nestedValue;
                }
            }
        }

        return [];
    }

    private normalizeAcademicHolidayDetailResponse(res: unknown): AcademicHolidayModel {
        if (res !== null && typeof res === "object" && !Array.isArray(res)) {
            const response = res as Record<string, unknown>;

            if (response.data !== null && typeof response.data === "object" && !Array.isArray(response.data)) {
                return response.data as AcademicHolidayModel;
            }
        }

        return res as AcademicHolidayModel;
    }

}
